/* eslint-disable no-console */
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useIsOnline } from 'react-use-is-online';

import { SocketData } from '../../types';
import { DeviceType } from '../../types/scheduler.types';
import { GetPresentationResponse } from '../../api-http/presentations';

import { errorMsg } from '../../configs';
import useSocket from '../../util/useSocket';
import { metaTitle } from '../../util/tabTitle';
import { useGetPublicForm } from '../../api-http/forms';
// import { handleRefresh } from '../../util/handleRefresh';
import SurveyPreview from '../../components/SurveyPreview';
import { EVENTS, SOCKET_EVENTS } from '../../util/constant';
import NotAssignPage from '../not-assign-page/NotAssignPage';
import DisplayError from '../../components/common/ApiErrorDisplay';
import schedulePresentations from '../../util/schedulePresentations';
import ErrorDisplayPage from '../error-display-page/ErrorDisplayPage';
import { EditorProvider } from '../../components/survey-editor/EditorProvider';
import PresentationPreview from '../../components/preview/PresentationPreview';
import {
  useGetCacheInterval,
  useGetDeviceData,
} from '../../api-http/scheduler';
import {
  AssetData,
  GetDisplayPresentationResponse,
  useGetDisplayPresentation,
} from '../../api-http/displays';
import {
  addItemToDb,
  clearAllInDb,
  createDatabase,
  getById,
  getData,
  transferDataAndClear,
} from '../../indexedDb';
import Dexie from 'dexie';
import DownloadProgress from './DownloadProgress';

function assetArraysAreEqual(
  array1: AssetData[],
  array2: AssetData[],
): boolean {
  // Check if the lengths are different
  if (array1.length !== array2.length) {
    return false;
  }

  // Compare by assetId (value equality) since array2 is always a freshly
  // fetched array of new object literals, so reference equality would
  // never match even when the underlying assets are unchanged.
  const ids1 = array1.map((asset) => asset.assetId).sort();
  const ids2 = array2.map((asset) => asset.assetId).sort();
  return ids1.every((id, index) => id === ids2[index]);
}

function isStringArray(value: unknown): value is string[] {
  return (
    Array.isArray(value) &&
    // every element must be a primitive string
    value.every((item) => typeof item === 'string')
  );
}

const ShowPresentationFormPreview: FC = () => {
  metaTitle('Preview');

  const { isOnline } = useIsOnline();
  const { deviceId } = useParams<{ deviceId: string }>();
  const { socket, socketErrors } = useSocket();

  const [isSyncing, setIsSyncing] = useState(false); // Indicate if there's a background caching is happening
  const [isCaching, setIsCaching] = useState(false); // Indicate assets caching status
  const [isInitialLoad, setIsInitialLoad] = useState(true); // If the page is loading for the first time
  const [isPreCacheNeed, setIsPreCacheNeed] = useState(true); // If the pages needed to pre cache assets
  const [downloadProgress, setDownloadProgress] = useState(0); // Asset download progress
  const [downloadAssetCount, setDownloadAssetCount] = useState('Loading...'); // Count of downloaded assets
  const [currentAssetName, setCurrentAssetName] = useState(''); // Name of asset currently downloading
  const [presentationId, setPresentationId] = useState<string | undefined>(''); // Presentation to be run.
  const [presentation, setPresentation] = useState<
    GetDisplayPresentationResponse | GetPresentationResponse
  >(); // Actual running presentation

  // Republish/KVS updates can emit more than one update for the same change
  // (e.g. duplicate socket events), each triggering cacheAssets(). Without
  // this, two overlapping runs both call clearAllInDb('newAssets') and race
  // on the same IndexedDB store, so the transfer at the end of the first run
  // can pick up assets the second run already wiped - leaving assetsList
  // empty (but still a truthy object) and the slide canvas blank. Chaining
  // runs behind this ref serializes them instead of letting them race.
  const cachingChainRef = useRef<Promise<void>>(Promise.resolve());

  //   const preCacheDurationMilliSec = 0;

  /**
   * Get the pre cache interval for the device
   */
  const {
    data: cacheIntervalData,
    error: cacheIntervalError,
    isLoading: isLoadingCacheInterval,
    refetch: refetchCacheInterval,
  } = useGetCacheInterval(deviceId);

  /**
   * Get the device data
   */
  const {
    data: deviceData,
    error: errorDeviceData,
    isLoading: isLoadingDeviceData,
    refetch: refetchDeviceData,
  } = useGetDeviceData(deviceId);

  /**
   * Get the form data
   */
  const {
    data: formData,
    isLoading: isLoadingForm,
    refetch: refetchForm,
  } = useGetPublicForm(deviceData?.form?._id);

  /**
   *
   */
  const {
    data,
    error,
    isLoading,
    refetch: refetchPresentation,
  } = useGetDisplayPresentation(
    presentationId || deviceData?.scheduler?.defaultPresentation,
  );

  const scheduleUpdate = useCallback(() => {
    if (
      deviceData &&
      deviceData.operatingType === DeviceType.SCHEDULED_DEVICE
    ) {
      const id = schedulePresentations({
        scheduler: deviceData.scheduler,
        cacheInterval: cacheIntervalData as number,
      });

      // Always set explicitly rather than comparing against the current
      // presentationId - this callback only depends on deviceData, so a
      // comparison here would read a stale presentationId captured at the
      // last deviceData change, not the current one. React already bails
      // out re-renders when the value is unchanged, so this is safe.
      setPresentationId(id || deviceData?.scheduler?.defaultPresentation);
    }
  }, [deviceData]);

  const restartPlayer = useCallback(() => {
    window.location.reload();
  }, []);

  const clearPlayerCache = useCallback(async () => {
    try {
      const indexedDb = await createDatabase();
      await clearAllInDb(indexedDb, 'assets');
      await clearAllInDb(indexedDb, 'newAssets');
    } finally {
      restartPlayer();
    }
  }, [restartPlayer]);

  const updateDeviceData = useCallback(
    (socketData: SocketData) => {
      const { data, event } = socketData;

      /**
       * Used to get the updated cache interval from the backend
       */
      if (
        event === EVENTS.setCacheInterval &&
        deviceId &&
        data[0] === deviceId
      ) {
        refetchCacheInterval();
      } else if (deviceId && isStringArray(data) && data.includes(deviceId)) {
        if (event === EVENTS.removeAppCache) {
          void clearPlayerCache();
          return;
        }

        if (event === EVENTS.restartDevice) {
          restartPlayer();
          return;
        }

        console.log('Device schedular details updated', data);
        /**
         * Used when user update the scheduler details
         * Or
         * When user update the device data
         */
        setIsPreCacheNeed(true);
        refetchDeviceData();
      }
    },
    [
      clearPlayerCache,
      deviceId,
      refetchCacheInterval,
      refetchDeviceData,
      restartPlayer,
    ],
  );

  /**
   * Used when user update the presentation
   */
  const updateAssignedPresentation = useCallback(
    (socketData: string) => {
      console.log(
        'updateAssignedPresentation ===>',
        presentationId,
        '++++',
        socketData,
        presentation,
      );
      const activePresentationId = presentationId || presentation?._id;
      if (
        activePresentationId &&
        String(socketData) === String(activePresentationId)
      ) {
        setIsPreCacheNeed(false);
        refetchPresentation();
      }
    },
    [presentationId, presentation?._id, refetchPresentation],
  );

  useEffect(() => {
    console.log('presentationId updated', presentationId);
  }, [presentationId]);

  useEffect(() => {
    if (socket) {
      socket.on(SOCKET_EVENTS.dataConnection, updateDeviceData);
      socket.on(SOCKET_EVENTS.updateDevice, updateDeviceData);
      socket.on(SOCKET_EVENTS.updatePresentation, updateAssignedPresentation);
    }
    return () => {
      socket.off(SOCKET_EVENTS.dataConnection, updateDeviceData);
      socket.off(SOCKET_EVENTS.updateDevice, updateDeviceData);
      socket.off(SOCKET_EVENTS.updatePresentation, updateAssignedPresentation);
    };
  }, [socket, updateDeviceData, updateAssignedPresentation]);

  useEffect(() => {
    // on every deviceData change fetch form data
    if (deviceData) {
      refetchForm();
    }

    // Create synchronized timer that aligns all devices to check at the same absolute time
    const createSynchronizedTimer = () => {
      const now = Date.now();
      const intervalMs = 1000; // 1 second for precise synchronization

      // Calculate delay to next 1-second boundary
      const delay = intervalMs - (now % intervalMs);

      let intervalId: NodeJS.Timeout | null = null;

      // First timeout to sync to the boundary
      const syncTimeoutId = setTimeout(() => {
        // Execute immediately when we hit the boundary
        scheduleUpdate();

        // Then start regular 1-second intervals
        intervalId = setInterval(() => {
          scheduleUpdate();
        }, intervalMs);
      }, delay);

      // Return an object with both IDs for proper cleanup
      return {
        timeoutId: syncTimeoutId,
        getIntervalId: () => intervalId,
      };
    };

    const timerIds = createSynchronizedTimer();

    // cleanup before the next effect (or on unmount):
    return () => {
      clearTimeout(timerIds.timeoutId);
      const intervalId = timerIds.getIntervalId();
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [deviceData, scheduleUpdate, refetchForm]);

  // useEffect(() => {
  //   if (presentationId && presentation?._id !== presentationId) {
  //     setIsPreCacheNeed(false);
  //     refetchPresentation();
  //   }
  // }, [presentationId]);

  const dataCachingFunc = async (indexedDb: Dexie) => {
    await transferDataAndClear(indexedDb);
    setPresentation(data);
    setIsSyncing(false);
    setIsCaching(false);
    if (isInitialLoad) {
      setIsInitialLoad(false);
    }
    if (isPreCacheNeed) {
      setIsPreCacheNeed(false);
    }
  };

  // Chains onto cachingChainRef so overlapping triggers run one after another
  // instead of racing on the shared IndexedDB 'newAssets' store.
  const cacheAssets = () => {
    const run = cachingChainRef.current.then(() => runCacheAssets());
    cachingChainRef.current = run.catch(() => {});
    return run;
  };

  const runCacheAssets = async () => {
    const indexedDb = await createDatabase();
    await clearAllInDb(indexedDb, 'newAssets');

    const initialData = await getData(indexedDb, 'assets');
    setIsCaching(!initialData);

    const assetsList = data?.assets;

    if (!assetsList) {
      return;
    }

    for (const [index, asset] of assetsList.entries()) {
      try {
        // Processing asset index
        setDownloadAssetCount(`${index}/${assetsList.length}`);
        setCurrentAssetName(
          asset?.assetSrc.split('/').pop()?.split('?')[0] ?? '',
        );

        const existingAsset = await getById(
          indexedDb,
          asset?.assetId,
          'assets',
        );

        if (existingAsset) {
          // Asset with the same ID already exists in the main DB
          // Transfer the existing blob to the newAssets DB
          await addItemToDb(
            indexedDb,
            { id: asset?.assetId, blob: existingAsset.blob },
            'newAssets',
          );
          setDownloadProgress(((index + 1) / assetsList.length) * 100);
        } else {
          // Asset with the same ID doesn't exist in the main DB
          // Fetch and store the new blob
          const response = await fetch(asset?.assetSrc, {
            mode: 'cors',
          });

          if (!response.ok) {
            console.log(`HTTP error! status: ${response.status}`);
          }

          const contentLength = response.headers.get('content-length');
          if (!contentLength) {
            console.log('Content Length not available!');
          }

          const totalSize = parseInt(contentLength || '0', 10); // Total size of the file
          let loadedSize = 0; // Amount of data loaded so far

          const reader = response.body?.getReader();
          if (!reader) {
            console.log('Reader not available!');
            return;
          }

          let mimeType = 'video/mp4'; // Default to video/mp4
          if (asset?.assetSrc.endsWith('.png')) {
            mimeType = 'image/png';
          } else if (
            asset?.assetSrc.endsWith('.jpg') ||
            asset?.assetSrc.endsWith('.jpeg')
          ) {
            mimeType = 'image/jpeg';
          }

          const chunks = []; // Array to store the chunks of data

          while (true) {
            const { done, value } = await reader.read();

            if (done) {
              break;
            }

            chunks.push(value);
            loadedSize += value.length;

            const progress = (loadedSize / totalSize) * 100; // Calculate the progress
            setDownloadProgress(progress);
          }

          const blob = new Blob(chunks, { type: mimeType });

          // @ts-ignore
          await addItemToDb(
            indexedDb,
            { id: asset?.assetId, blob: blob },
            'newAssets',
          );
        }
      } catch (error) {
        console.log('Reading Error', error);
      }
    }

    await dataCachingFunc(indexedDb);
  };

  useEffect(() => {
    let isCancelled = false;
    if (data) {
      const oldAssets = presentation?.assets || [];
      const newAssets = data?.assets || [];
      const areArraysEqual = assetArraysAreEqual(oldAssets, newAssets);

      if (isInitialLoad || !areArraysEqual) {
        setIsSyncing(true);
        cacheAssets().finally(() => {
          if (!isCancelled) {
            setIsSyncing(false);
          }
        });
      } else {
        // Assets are unchanged, but the presentation content itself (slide
        // text, kvs, layout, etc.) may still have changed, so it must not
        // be skipped just because there's nothing new to cache/download.
        setPresentation(data);
        if (isPreCacheNeed) {
          setIsPreCacheNeed(false);
        }
      }
    }
    return () => {
      isCancelled = true;
    };
  }, [data]);

  if (!deviceData && !isLoadingDeviceData && !isLoadingCacheInterval) {
    if (errorDeviceData || error || cacheIntervalError) {
      return (
        <DisplayError
          msg={
            errorDeviceData?.msg ||
            error?.msg ||
            cacheIntervalError?.msg ||
            errorMsg.COMMON_ERROR
          }
        />
      );
    } else if (isOnline && socketErrors.length) {
      return <ErrorDisplayPage errorsArray={socketErrors} />;
    }
  }

  if (deviceData && !deviceData?.operatingType) {
    return <NotAssignPage />;
  }

  const isScheduledDevice =
    deviceData?.operatingType === DeviceType.SCHEDULED_DEVICE;

  // Once a presentation has rendered at least once, keep it on screen while
  // a republish or KVS update refetches/re-caches in the background —
  // falling through to the loading screen here would blank the display
  // between updates instead of swapping only once the new one is ready.
  if (isScheduledDevice && presentation) {
    return (
      <PresentationPreview presentation={presentation} isSyncing={isSyncing} />
    );
  }

  if (
    isLoading ||
    (isScheduledDevice && isLoadingDeviceData) ||
    isLoadingForm ||
    isCaching ||
    isInitialLoad
  ) {
    return (
      <DownloadProgress
        downloadAssetCount={downloadAssetCount}
        downloadProgress={downloadProgress}
        currentAssetName={currentAssetName}
      />
    );
  }

  if (deviceData?.operatingType === DeviceType.FORM_DEVICE) {
    if (formData?.formBody) {
      if (isLoadingForm) {
        return (
          <Box
            sx={{ display: 'flex', height: '100vh' }}
            justifyContent="center"
            alignItems="center"
          >
            <CircularProgress size={100} />
          </Box>
        );
      }

      return (
        <EditorProvider>
          <SurveyPreview data={formData} formId={deviceData?.form?._id} />
        </EditorProvider>
      );
    }
  }

  return null;
};

export default ShowPresentationFormPreview;
